// Hero Slider - Simple & Reliable
(function() {
  'use strict';

  class HeroSlider {
    constructor(element) {
      this.slider = element;
      this.track = this.slider.querySelector('.hero-slider__track');
      this.slides = Array.from(this.slider.querySelectorAll('.hero-slider__slide'));

      if (!this.track || this.slides.length === 0) return;

      this.currentIndex = 0;
      this.autoplayInterval = null;

      // Read settings from data attributes
      this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
      this.autoplayDelay = (parseInt(this.slider.dataset.autoplaySpeed) || 5) * 1000;

      this.isDragging = false;
      this.isMouseDown = false;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.dragDistance = 0;
      this.hasMoved = false;

      this.init();
    }

    init() {
      // Only setup if we have multiple slides
      if (this.slides.length <= 1) return;

      this.setupDragScroll();
      this.preventImageDrag();

      // Autoplay setup
      if (this.autoplayEnabled) {
        this.startAutoplay();

        // Pause on hover
        this.slider.addEventListener('mouseenter', () => this.stopAutoplay());
        this.slider.addEventListener('mouseleave', () => this.startAutoplay());

        // Pause when tab hidden
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.stopAutoplay();
          } else if (this.autoplayEnabled) {
            this.startAutoplay();
          }
        });
      }
    }

    setupDragScroll() {
      // Mouse events
      this.slider.addEventListener('mousedown', (e) => {
        // Don't interfere with link/button clicks
        if (e.target.closest('a, button')) {
          return;
        }

        this.isMouseDown = true;
        this.hasMoved = false;
        this.startX = e.pageX;
        this.startScrollLeft = this.slider.scrollLeft;
        this.dragDistance = 0;
        this.stopAutoplay();
      });

      this.slider.addEventListener('mouseleave', () => {
        if (this.isDragging) {
          this.isDragging = false;
          this.isMouseDown = false;
          // Remove dragging class first to re-enable smooth scroll-behavior
          this.slider.classList.remove('is-dragging');
          // Use setTimeout to ensure CSS smooth scroll-behavior is re-applied
          setTimeout(() => {
            this.snapToNearestSlide();
          }, 10);
          if (this.autoplayEnabled) this.startAutoplay();
        } else {
          this.isMouseDown = false;
        }
      });

      this.slider.addEventListener('mouseup', () => {
        if (this.isDragging) {
          this.isDragging = false;
          this.isMouseDown = false;
          // Remove dragging class first to re-enable smooth scroll-behavior
          this.slider.classList.remove('is-dragging');
          // Use setTimeout to ensure CSS smooth scroll-behavior is re-applied
          setTimeout(() => {
            this.snapToNearestSlide();
          }, 10);
          if (this.autoplayEnabled) this.startAutoplay();
        } else {
          this.isMouseDown = false;
        }
      });

      this.slider.addEventListener('mousemove', (e) => {
        if (!this.isMouseDown) return;

        // Calculate drag distance (how far from start position)
        this.dragDistance = this.startX - e.pageX;

        // Only start "dragging" if moved more than 5 pixels (prevents accidental drags on clicks)
        if (!this.isDragging && Math.abs(this.dragDistance) > 5) {
          this.isDragging = true;
          this.hasMoved = true;
          this.slider.classList.add('is-dragging');
        }

        if (this.isDragging) {
          e.preventDefault();
          // Update scroll position to follow mouse exactly
          this.slider.scrollLeft = this.startScrollLeft + this.dragDistance;
        }
      });

      // Touch events
      this.slider.addEventListener('touchstart', () => {
        this.stopAutoplay();
      }, { passive: true });

      this.slider.addEventListener('touchend', () => {
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      }, { passive: true });

      // Update current index on scroll
      this.slider.addEventListener('scroll', () => {
        this.updateCurrentIndex();
      }, { passive: true });
    }

    preventImageDrag() {
      this.slides.forEach(slide => {
        const images = slide.querySelectorAll('img');
        images.forEach(img => {
          img.addEventListener('dragstart', (e) => e.preventDefault());
        });

        const links = slide.querySelectorAll('a');
        links.forEach(link => {
          link.addEventListener('click', (e) => {
            // Only prevent click if user actually dragged (moved the slider)
            if (this.hasMoved) {
              e.preventDefault();
              this.hasMoved = false; // Reset for next interaction
            }
          });
        });
      });
    }

    snapToNearestSlide() {
      const slideWidth = this.slider.offsetWidth;

      // Calculate the starting slide index (where we started dragging from)
      const startingSlideIndex = Math.round(this.startScrollLeft / slideWidth);

      // Threshold-based logic: if dragged more than 30% of slide width, go to next/prev
      const dragThreshold = slideWidth * 0.3;
      let targetIndex;

      if (Math.abs(this.dragDistance) > dragThreshold) {
        // Determine direction: positive dragDistance = dragged left (go to next slide)
        if (this.dragDistance > 0) {
          targetIndex = Math.min(startingSlideIndex + 1, this.slides.length - 1);
        } else {
          targetIndex = Math.max(startingSlideIndex - 1, 0);
        }
      } else {
        // Snap back to starting slide if drag was less than threshold
        targetIndex = startingSlideIndex;
      }

      // Ensure target index is within bounds
      targetIndex = Math.max(0, Math.min(targetIndex, this.slides.length - 1));

      // Smooth scroll to target slide
      this.goToSlide(targetIndex);
    }

    updateCurrentIndex() {
      const slideWidth = this.slider.offsetWidth;
      const scrollPosition = this.slider.scrollLeft;
      this.currentIndex = Math.round(scrollPosition / slideWidth);
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;

      const slideWidth = this.slider.offsetWidth;
      this.slider.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
      this.currentIndex = index;
    }

    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.slides.length > 1 && this.autoplayEnabled) {
        this.autoplayInterval = setInterval(() => {
          this.nextSlide();
        }, this.autoplayDelay);
      }
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }
  }

  // Initialize slider
  function initSlider() {
    const sliderElement = document.querySelector('.hero-slider');
    if (sliderElement) {
      const sliderInstance = new HeroSlider(sliderElement);

      // Store instance for potential external access
      sliderElement.heroSlider = sliderInstance;
    }
  }

  // Init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlider);
  } else {
    initSlider();
  }
})();
